import React, {
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { get, isArray, isEqual } from 'lodash';
import { Config, Store } from '@oscar/request';
import { useDeepCompareEffect } from '@oscar/utils';
import { AuthState, AuthProps, AuthConfig } from './types';
import { createContext } from './AuthContext';
import { useSSO } from './hooks';

const CURRENT_ROLE_KEY = 'current_roles';

export default function createAuth<T extends AuthState>(
  config: AuthConfig<T['permission'], T['policy'], T['role']>
) {
  const AuthContext = createContext<T['permission'], T['policy'], T['role']>();
  const AuthProvider = (
    props: AuthProps<T['permission'], T['policy'], T['role']>
  ) => {
    const { role, children } = props;
    const [ssoProvider] = useSSO(config?.provider?.SSO);
    const [userRoles, setUserRoles] = useState<T['role'][]>();
    const [isLoaded, setIsLoaded] = useState<boolean>(!config?.provider?.SSO);

    const configRoles = useMemo(() => {
      return config?.getUserRole?.(Config.getInstance().getTokenValue());
    }, [Config.getInstance().getTokenValue()]);

    const userInformation = useMemo(() => {
      return config?.getUserInformation?.(Config.getInstance().getTokenValue());
    }, [Config.getInstance().getTokenValue()]);

    const permissions = useMemo(() => {
      return config?.getPermission?.(Config.getInstance().getTokenValue());
    }, [Config.getInstance().getTokenValue()]);

    const getUserRoles = useCallback(() => {
      return userRoles || [];
    }, [userRoles]);

    const getValidRoles = useCallback(() => {
      return configRoles || [];
    }, [configRoles]);

    const switchRoles = useCallback(
      (roles: T['role'] | T['role'][]) => {
        if (!config.switchRole) {
          throw new Error(
            `You have to setting switch role mode before using this method`
          );
        }
        const current = roles instanceof Array ? roles : [roles];
        const validRoles = current.filter((r) => configRoles?.includes(r));
        if (isEqual(validRoles, current)) {
          Store.setItem(CURRENT_ROLE_KEY, JSON.stringify(current));
          setUserRoles(current);
        } else {
          throw new Error(`Roles: ${current.join()} are not valided`);
        }
      },
      [userRoles]
    );

    const getUserInformation = useCallback(() => {
      return userInformation || {};
    }, [userInformation]);

    const checkRole: (
      policy: T['policy'],
      action: T['permission'],
      specificRole?: T['role'] | T['role'][]
    ) => boolean = useCallback(
      (policy, action, specificRole) => {
        const _permission = permissions || role;
        if (userRoles) {
          let currentRoless = [...userRoles];
          if (specificRole) {
            if (
              typeof specificRole === 'string' &&
              !userRoles.includes(specificRole)
            ) {
              return false;
            } else if (isArray(specificRole)) {
              currentRoless = currentRoless.filter((item) =>
                currentRoless.includes(item)
              );
            }
          }
          for (const userRole of currentRoless) {
            const _role = _permission?.find((item) => item.name === userRole);
            if (_role?.permissions && action) {
              const items: string[] = get(_role?.permissions, policy, []);
              if (items.includes(action)) {
                return true;
              }
            }
          }
        }
        return false;
      },
      [role, permissions]
    );

    useDeepCompareEffect(() => {
      if (config?.switchRole) {
        try {
          const currentRoles = JSON.parse(
            Store.getItem(CURRENT_ROLE_KEY) || ''
          );
          switchRoles(currentRoles);
        } catch (error) {
          setUserRoles(
            config?.switchRole ? configRoles?.slice?.(0, 1) : configRoles
          );
        }
      } else {
        setUserRoles(configRoles);
      }
    }, [configRoles]);

    const authorizeData = useCallback((token: string, url?: string) => {
      const key = Config.getInstance().getTokenKey(url);
      Store.setItem(key, token);
    }, []);

    useEffect(() => {
      if (ssoProvider) {
        setIsLoaded(true);
      }
    }, [ssoProvider]);

    const logout = useCallback(
      (url?: string) => {
        Config.getInstance().removeSession(url);
        if (ssoProvider) {
          ssoProvider.logout();
        }
      },
      [ssoProvider]
    );

    return (
      <AuthContext.Provider
        value={{
          checkRole,
          getUserRoles,
          getValidRoles,
          switchRoles,
          getUserInformation,
          authorizeData,
          logout,
          ssoProvider,
        }}
      >
        {children}
        {!isLoaded && (
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            {config?.loading}
          </div>
        )}
      </AuthContext.Provider>
    );
  };

  const useAuth = () => {
    const formContext = useContext(AuthContext);
    return [formContext];
  };

  return {
    AuthProvider,
    useAuth,
  };
}
